import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Phone, Share2, MapPin, ChevronDown } from "lucide-react";
import "./staffPage.css";
import generalRequest from "../../request/generalRequest";
import StaffPageSkeleton from "./staffSkeleton/staffPageSkeleton";
import BookingModal from "../bookingModal/bookingModal";
import { CombinedContext } from "../../context/combinedContext";
import translations from "../../translations/translations";
import ShopMapPreview from "../../utils/shopMap/shopMapPreview";

/* ── helpers ── */
function formatPhone(number) {
	if (!number) return "";
	const digits = number.replace(/\D/g, "");
	if (digits.length === 11 && digits.startsWith("1")) {
		return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
	}
	return number.startsWith("+") ? number : `+${number}`;
}

function formatTime24To12(time24) {
	if (!time24) return "";
	const [h, m] = time24.split(":").map(Number);
	const hour12 = h % 12 || 12;
	const minutes = m.toString().padStart(2, "0");
	return `${hour12}:${minutes} ${h >= 12 ? "PM" : "AM"}`;
}

function formatUrl(url) {
	if (!url) return "";
	return url.startsWith("http://") || url.startsWith("https://") ? url : `https://${url}`;
}

/* ── Sidebar — rendered in mobile Info panel AND desktop sidebar ── */
function SidebarContent({ shopData, language, t, navigate, shopId }) {
	const addressQuery = encodeURIComponent(`${shopData.address}, ${shopData.city}, ${shopData.country}`);
	return (
		<div className="sf-profile-sidebar">
			<div className="sf-profile-sidebar-section">
				<h3 className="sf-shop-sidebar-title">{translations[language].shopPage.sidebar.location}</h3>
						<div className="sf-shop-map-wrap">
				<ShopMapPreview lat={shopData.lat} lng={shopData.lng} />
				</div>
				<a
							className="sf-shop-directions-btn"
							href={`https://www.google.com/maps/dir/?api=1&destination=${addressQuery}`}
							target="_blank"
							rel="noopener noreferrer"
						>
							{translations[language].shopPage.sidebar.getDirections}
						</a>
			</div>

			{shopData.description && (
				<div className="sf-profile-sidebar-section">
					<div className="sf-shop-sidebar-title-row">
						<h3 className="sf-shop-sidebar-title">{t[language].shopPage.sidebar.description}</h3>
					</div>
					<p className="sf-shop-description">{shopData.description}</p>
				</div>
			)}

			<div className="sf-shop-sidebar-section">
				<div className="sf-shop-sidebar-title-row">
					<h3 className="sf-shop-sidebar-title">{t[language].shopPage.sidebar.staff}</h3>
				</div>
				<div className="sf-profile-staff-list">
					{shopData.staffs?.map((b) => (
						<div
							className="sf-profile-staff-item"
							key={b.staffId}
							onClick={() => navigate(`/shops/${shopData.shopId}/staff/${b.staffId}`)}
							style={{ cursor: "pointer" }}
						>
							{b.photo ? (
								<img className="sf-profile-staff-avatar" src={b.photo} alt={b.firstName} />
							) : (
								<div className="sf-profile-staff-avatar-fallback">
									<span>{b.firstName?.charAt(0).toUpperCase()}</span>
								</div>
							)}
							<div className="sf-profile-staff-name">{b.firstName}</div>
						</div>
					))}
				</div>
			</div>

			<div className="sf-profile-sidebar-section">
				<div className="sf-shop-sidebar-title-row">
					<h3 className="sf-profile-sidebar-title">{t[language].profile.sidebar.contact.title}</h3>
				</div>
				<hr className="sf-shop-divider" />

				{shopData.phone && (
					<p className="sf-profile-contact">
						<Phone size={16} strokeWidth={1.6} className="sf-profile-contact-icon" />
						<span>{formatPhone(shopData.phone)}</span>
					</p>
				)}

				{shopData.openingHours?.length > 0 && (
					<>
						<hr className="sf-shop-divider" />
						<div className="sf-shop-sidebar-title-row">
							<h3 className="sf-profile-sidebar-title">{t[language].profile.sidebar.hours.title}</h3>
						</div>
						<div className="sf-shop-hours-full">
							{shopData.openingHours.map((day, i) => {
								const todayIndex = (new Date().getDay() + 6) % 7;
								const isToday = todayIndex === i;
								const dayName = language === "es" ? day.dayES : day.dayEN;
								return (
									<div key={i} className={`sf-shop-hours-row ${isToday ? "is-today" : ""}`}>
										<div className="sf-shop-hours-day">{dayName}</div>
										<div className="sf-shop-hours-time">
											{day.closed
												? t[language].profile.sidebar.hours.closed
												: `${formatTime24To12(day.open)} – ${formatTime24To12(day.close)}`}
										</div>
									</div>
								);
							})}
						</div>
					</>
				)}

				{shopData.socialLinks && Object.values(shopData.socialLinks).some(Boolean) && (
					<div>
						<hr className="sf-shop-divider" />
						<h3 className="sf-profile-sidebar-title">{t[language].shopPage.sidebar.socialTitle}</h3>
						<div className="sf-shop-socials">
							{shopData.socialLinks.instagram && (
								<div className="sf-social-item">
									<a href={formatUrl(shopData.socialLinks.instagram)} target="_blank" rel="noopener noreferrer" className="sf-social-link">
										<img src="https://cdn-icons-png.flaticon.com/512/1384/1384031.png" alt="Instagram" />
									</a>
									<p>Instagram</p>
								</div>
							)}
							{shopData.socialLinks.facebook && (
								<div className="sf-social-item">
									<a href={formatUrl(shopData.socialLinks.facebook)} target="_blank" rel="noopener noreferrer" className="sf-social-link">
										<img src="https://cdn-icons-png.flaticon.com/512/1384/1384005.png" alt="Facebook" />
									</a>
									<p>Facebook</p>
								</div>
							)}
							{shopData.socialLinks.tiktok && (
								<div className="sf-social-item">
									<a href={formatUrl(shopData.socialLinks.tiktok)} target="_blank" rel="noopener noreferrer" className="sf-social-link">
										<img src="https://cdn-icons-png.flaticon.com/512/3046/3046120.png" alt="TikTok" />
									</a>
									<p>TikTok</p>
								</div>
							)}
							{shopData.socialLinks.website && (
								<div className="sf-social-item">
									<a href={formatUrl(shopData.socialLinks.website)} target="_blank" rel="noopener noreferrer" className="sf-social-link">
										<img src="https://cdn-icons-png.flaticon.com/512/545/545674.png" alt="Website" />
									</a>
									<p>{t[language].shopPage.sidebar.websiteTitle}</p>
								</div>
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
}

/* ── Carousel ── */
function StaffCarousel({ images, onShare, shareCopied, onBack }) {
	const trackRef = useRef(null);
	const [idx, setIdx] = useState(0);
	const isDragging = useRef(false);
	const startX = useRef(0);
	const deltaX = useRef(0);

	const hasImages = images && images.length > 0;
	const goTo = (n) => setIdx((n + images.length) % images.length);

	useEffect(() => {
		if (trackRef.current) {
			trackRef.current.style.transform = `translateX(${-idx * 100}%)`;
		}
	}, [idx]);

	const onTouchStart = (e) => {
		isDragging.current = true;
		startX.current = e.touches ? e.touches[0].clientX : e.clientX;
		deltaX.current = 0;
	};
	const onTouchMove = (e) => {
		if (!isDragging.current) return;
		deltaX.current = (e.touches ? e.touches[0].clientX : e.clientX) - startX.current;
		if (trackRef.current) {
			trackRef.current.style.transition = "none";
			trackRef.current.style.transform = `translateX(calc(${-idx * 100}% + ${deltaX.current}px))`;
		}
	};
	const onTouchEnd = () => {
		isDragging.current = false;
		if (Math.abs(deltaX.current) > 60) deltaX.current < 0 ? goTo(idx + 1) : goTo(idx - 1);
		if (trackRef.current) {
			trackRef.current.style.transition = "transform 300ms ease";
			trackRef.current.style.transform = `translateX(${-idx * 100}%)`;
		}
		deltaX.current = 0;
	};

	return (
		<div className="sf-shop-carousel" aria-roledescription="carousel">
			<div className="sf-carousel-top-bar">
				<button className="sf-carousel-overlay-btn" onClick={onBack}>
					<ArrowLeft size={18} />
				</button>
				<button className={`sf-carousel-overlay-btn ${shareCopied ? "sfp-share-copied" : ""}`} onClick={onShare}>
					<Share2 size={18} />
				</button>
			</div>

			<div
				className="sf-shop-carousel-viewport"
				onTouchStart={onTouchStart}
				onTouchMove={onTouchMove}
				onTouchEnd={onTouchEnd}
				onMouseDown={(e) => {
					e.preventDefault();
					onTouchStart(e);
					window.addEventListener("mousemove", onTouchMove);
					window.addEventListener(
						"mouseup",
						() => {
							onTouchEnd();
							window.removeEventListener("mousemove", onTouchMove);
						},
						{ once: true },
					);
				}}
			>
				{hasImages ? (
					<div className="sf-shop-carousel-track" ref={trackRef}>
						{images.map((img, i) => (
							<div className="sf-shop-carousel-slide" key={i} aria-hidden={i !== idx}>
								<img className="sf-shop-carousel-img" src={img} alt={`Gallery ${i + 1}`} draggable={false} />
							</div>
						))}
					</div>
				) : (
					<div className="sfp-carousel-empty">
						<p>📷</p>
					</div>
				)}

				{hasImages && images.length > 1 && (
					<>
						<button className="sf-shop-carousel-arrow sf-shop-carousel-arrow--left" onClick={() => goTo(idx - 1)}>
							‹
						</button>
						<button className="sf-shop-carousel-arrow sf-shop-carousel-arrow--right" onClick={() => goTo(idx + 1)}>
							›
						</button>
					</>
				)}
			</div>

			{hasImages && images.length > 1 && (
				<div className="sf-shop-carousel-dots">
					{images.map((_, i) => (
						<button
							key={i}
							className={`sf-shop-carousel-dot ${i === idx ? "is-active" : ""}`}
							onClick={() => goTo(i)}
							aria-label={`Go to slide ${i + 1}`}
						/>
					))}
				</div>
			)}
		</div>
	);
}

/* ============================================================
   MAIN COMPONENT
============================================================ */
const StaffPage = () => {
	const { shopId, staffId } = useParams();
	const { selectedService, setSelectedService, language } = useContext(CombinedContext);

	const [staff, setStaff] = useState(null);
	const [shopData, setShopData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [isBookingOpen, setIsBookingOpen] = useState(false);
	const [activeCategory, setActiveCategory] = useState(null);
	const [shareCopied, setShareCopied] = useState(false);
	const [openCategories, setOpenCategories] = useState({});

	const categoryRefs = useRef({});
	const tabsRef = useRef(null);

	const navigate = useNavigate();

	useEffect(() => {
		if (!staffId) return;
		const load = async () => {
			try {
				const res = await generalRequest.getStaffInfoPublic(staffId);
				const s = res.staff;
				setStaff({
					staffId: s.staffId,
					firstName: s.firstName,
					lastName: s.lastName,
					image: s.photo || null,
					bio: s.bio || "",
					phone: s.phone || "",
					services: s.services || [],
				});
				setShopData({
					...s.shop,
					lat: s.shop.lat,
					lng: s.shop.lng,
				});
				setLoading(false);
			} catch (err) {
				console.error("Failed to fetch staff:", err);
				setLoading(false);
			}
		};
		load();
	}, [staffId]);

	const servicesByCategory =
		!loading && staff && shopData
			? (shopData.serviceCategories || [])
					.map((cat) => ({
						category: cat,
						services: staff.services.filter((s) => s.categoryId === cat.categoryId),
					}))
					.filter(({ services }) => services.length > 0)
			: [];

	useEffect(() => {
		if (!servicesByCategory.length) return;
		setOpenCategories(Object.fromEntries(servicesByCategory.map(({ category }) => [category.categoryId, true])));
	}, [shopData]);

	const toggleCategory = (id) => setOpenCategories((prev) => ({ ...prev, [id]: !prev[id] }));

	/* keep activeCategory valid */
	useEffect(() => {
		if (!servicesByCategory.length) return;
		const stillValid = servicesByCategory.some((g) => g.category.categoryId === activeCategory);
		if (!stillValid) setActiveCategory(servicesByCategory[0].category.categoryId);
	}, [servicesByCategory, activeCategory]);

	/* scroll-spy */
	useEffect(() => {
		if (!servicesByCategory.length) return;
		const firstId = servicesByCategory[0].category.categoryId;
		const stickyHeight = tabsRef.current?.offsetHeight || 0;
		const observer = new IntersectionObserver(
			(entries) => {
				const visible = entries.filter((e) => e.isIntersecting);
				if (visible.length) {
					const closest = visible.reduce((a, b) => (Math.abs(a.boundingClientRect.top) < Math.abs(b.boundingClientRect.top) ? a : b));
					setActiveCategory(closest.target.getAttribute("data-category-id"));
				} else if (window.scrollY < 200) {
					setActiveCategory(firstId);
				}
			},
			{ rootMargin: `-${stickyHeight + 8}px 0px -70% 0px`, threshold: [0.01, 0.1, 0.2] },
		);
		Object.values(categoryRefs.current).forEach((el) => el && observer.observe(el));
		return () => observer.disconnect();
	}, [servicesByCategory]);

	/* scroll active tab button into view */
	useEffect(() => {
		if (!activeCategory || !tabsRef.current) return;
		const btn = tabsRef.current.querySelector(`[data-category-tab="${activeCategory}"]`);
		if (btn) tabsRef.current.scrollTo({ left: btn.offsetLeft, behavior: "smooth" });
	}, [activeCategory]);

	if (loading || !staff || !shopData) return <StaffPageSkeleton />;

	const handleShare = () => {
		const url = `https://reservasinfila.com/shops/${shopData?.shopId}/staff/${staff?.staffId}`;
		if (navigator.share) {
			navigator.share({ url });
		} else {
			navigator.clipboard.writeText(url);
			setShareCopied(true);
			setTimeout(() => setShareCopied(false), 2000);
		}
	};

	const handleBack = () => navigate(`/shops/${shopId}`, { state: { fromStaff: true } });

	const openBookingForService = (serviceId) => {
		setSelectedService(serviceId);
		setIsBookingOpen(true);
	};

	return (
		<div className="sfp-page">
			{/* ── CAROUSEL ── */}

			{/* ── TWO-COLUMN GRID ── */}
			<div className="sfp-content">
							

				{/* LEFT / MAIN */}
				<div className="sfp-main">
					<StaffCarousel images={shopData.images || []} onShare={handleShare} shareCopied={shareCopied} onBack={handleBack} />
					<div className="sf-shop-profile-mobile-div">
						{/* shop name + address */}
						<div className="sf-profile-section-name">
							<h1 className="sf-profile-name">{shopData.name}</h1>
						</div>
						<p className="sf-profile-location">
							{/* <MapPin size={16} strokeWidth={1.8} className="sf-location-pin" /> */}
							{shopData.address}
						</p>
						{shopData.addressDescription && <p className="sf-profile-location sf-profile-location--line2">{shopData.addressDescription}</p>}

						{/* staff identity */}
						<div className="sfc-identity">
							<div className="sfc-avatar-wrap">
								{staff.image ? (
									<img src={staff.image} alt="" className="sfc-avatar" />
								) : (
									<div className="sfc-avatar-fallback">{staff.firstName?.charAt(0)}</div>
								)}
							</div>
							<div className="sfc-identity-text">
								<h2 className="sfc-identity-name">
									{staff.firstName} {staff.lastName}
								</h2>
								{staff.bio && <p className="sfc-identity-bio">{staff.bio}</p>}
								{staff.phone && (
									<p className="sfc-identity-phone">
										<Phone size={13} strokeWidth={1.8} />
										{formatPhone(staff.phone)}
									</p>
								)}
							</div>
						</div>

						{/* services title */}
						<h2 className="sf-profile-section-title">{translations[language].shopPage.main.services}</h2>

						{/* category tabs */}
						{servicesByCategory.length > 0 && (
							<div ref={tabsRef} className="sf-profile-tabs">
								{servicesByCategory.map(({ category }) => (
									<button
										key={category.categoryId}
										data-category-tab={category.categoryId}
										className={`sf-profile-tab-btn ${activeCategory === category.categoryId ? "active" : ""}`}
										onClick={() => {
											setActiveCategory(category.categoryId);
											categoryRefs.current[category.categoryId]?.scrollIntoView({ behavior: "smooth", block: "start" });
										}}
									>
										{category.name?.[language]}
									</button>
								))}
							</div>
						)}

						<div className="sf-profile-service-list">
							{servicesByCategory.map(({ category, services }) => {
								const isOpen = openCategories[category.categoryId] !== false;
								return (
									<div
										key={category.categoryId}
										ref={(el) => (categoryRefs.current[category.categoryId] = el)}
										data-category-id={category.categoryId}
										className="sf-service-category-section"
									>
										{servicesByCategory.length > 1 && (
											<div
												className="sf-service-category-div"
												onClick={() => toggleCategory(category.categoryId)}
												style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
											>
												<h3 className="sf-service-category-title">{category.name?.[language]}</h3>
												<ChevronDown size={16} className={`sf-category-chevron ${isOpen ? "open" : ""}`} />
											</div>
										)}

										<div className="sf-category-body" style={{ maxHeight: isOpen ? "2000px" : "0px" }}>
											{services.map((s) => (
												<div key={s.serviceId}>
													<div className="sf-profile-service-item">
														<div className="sf-profile-service-info">
															<div className="sf-profile-service-name">
																{s.name}
																{s.isHomeService && (
																	<span className="sf-profile-service-home">
																		{" · "}
																		{language === "es" ? "Domicilio" : "Home service"}
																	</span>
																)}
															</div>
															{s.description && <div className="sf-profile-service-duration">{s.description}</div>}
														</div>
														<div className="sfp-service-right">
															<div className="sfp-service-price-block">
																<div className="sf-profile-service-price">
																	{s.currency} {s.price}
																</div>
																<div className="sf-profile-service-duration">{s.duration} min</div>
															</div>
															<button className="sfp-book-btn" onClick={() => openBookingForService(s.serviceId)}>
																{translations[language].staffPage.book}
															</button>
														</div>
													</div>
													<div className="sf-service-divider-shop" aria-hidden="true" />
												</div>
											))}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</div>

				{/* RIGHT — desktop sidebar */}
				<div className="sfp-sidebar-desktop">
					<SidebarContent shopData={shopData} language={language} t={translations} navigate={navigate} shopId={shopId} />
				</div>
			</div>

			{isBookingOpen && (
				<BookingModal
					open={isBookingOpen}
					onClose={() => setIsBookingOpen(false)}
					availabilityId={shopData.shopId}
					shopData={shopData}
					initialServiceId={selectedService}
					initialstaffId={staff.staffId}
				/>
			)}
		</div>
	);
};

export default StaffPage;
